import React, { useEffect, useState } from 'react';
import Input from '../Input/Input';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { ButtonSize, ButtonStyle, IconsTypes } from '../../utils/props';
import { useTranslation } from 'react-i18next';

export interface EditableCellProps {
  rowId: number;
  field: 'interval-start' | 'interval-end';
  value: string;
  currentlyEditing: { rowId: number; field: 'interval-start' | 'interval-end' } | null;
  setCurrentlyEditing: (editing: { rowId: number; field: 'interval-start' | 'interval-end' } | null) => void;
  loading: boolean;
  editIndex: (id: number, field: 'interval-start' | 'interval-end', value: string) => Promise<void>;
  disabled?: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
  rowId,
  field,
  value,
  currentlyEditing,
  setCurrentlyEditing,
  loading,
  editIndex,
  disabled
}) => {
  const { t } = useTranslation('ns');

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const regex = /^$|^(?:[1-9]|[12]\d|3[01])$/;
    if (regex.test(newValue)) {
      setLocalValue(newValue);
    }
  };

  const isEditing =
    currentlyEditing &&
    currentlyEditing.rowId === rowId &&
    currentlyEditing.field === field;

  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
    }
  }, [value, isEditing]);

  const handleClick = () => {
    if (isEditing) {
      editIndex(rowId, field, localValue);
    } else {
      setCurrentlyEditing({ rowId, field });
      setLocalValue(value);
    }
  };

  const getButtonIcon = () => {
    if (disabled) {
      return (
        <Icon
          icon={IconsTypes.Edit}
          size={15}
          className="fill-gray-600 w-4 h-4 m-0.25 cursor-pointer"
        />
      );
    }
    return isEditing ? (
      <Icon
        icon={IconsTypes.Check}
        size={15}
        className="fill-white w-4 h-4 m-0.25 cursor-pointer"
      />
    ) : (
      <Icon
        icon={IconsTypes.Edit}
        size={15}
        className="fill-blue-900 w-4 h-4 m-0.25 cursor-pointer"
      />
    );
  };

  return (
    <div className="flex gap-3">
      <Input
        type='number'
        labelClass="text-left"
        disabled={!isEditing}
        value={localValue}
        onChange={handleIntervalChange}
        title={t(field)}
        className="w-[150px]"
      />
      <Button
        size={ButtonSize.Default}
        variant={isEditing ? ButtonStyle.SafePrimary : ButtonStyle.Secondary}
        loading={loading && !!isEditing}
        onClick={handleClick}
        className="py-[10px] disabled:fill-gray-300 disabled:bg-gray-200"
        disabled={disabled}
      >
        {getButtonIcon()}
      </Button>
    </div>
  );
};

export default EditableCell;
